import { useState, useEffect } from 'react'
import { cx, emailRegex } from '@/utils/strings'

import SubmittedImage from '@/icons/icon-sent-mail.svg'
import Link from '@/components/link-component'
import Form from '@/components/forms/form'
import { useTranslations } from '@/utils/locale'
import TextField from './fields/text-field'
import TextArea from './fields/text-area'
import SelectField from './fields/select-field'
import RadioFieldset from './fields/radio-fieldset'

interface ConfirmationValues {
  val: string
  el?: HTMLInputElement
}

export default function ContactSupport() {
  const { t } = useTranslations()
  const [submitted, setSubmitted] = useState(false)
  const [email1, setEmail1] = useState<ConfirmationValues | null>(null)
  const [email2, setEmail2] = useState<ConfirmationValues | null>(null)

  const products = [
    { key: 'Password Manager', label: t('Password Manager') },
    { key: 'Secrets Manager', label: t('Secrets Manager') },
    { key: 'Passwordless.dev', label: t('Passwordless.dev') },
    { key: 'Authenticator', label: t('Authenticator') },
  ]
  const subjects = [
    { key: 'Unable to access my account', label: t('Unable to access my account') },
    { key: 'Technical Support', label: t('Technical Support') },
    { key: 'Billing Support', label: t('Billing Support') },
    {
      key: 'Disabled Subscription or Organization',
      label: t('Disabled Subscription or Organization'),
    },
    { key: 'Other', label: t('Other') },
  ]

  useEffect(() => {
    if (email1 && email2) {
      if (email1.val === email2.val) {
        email2.el?.setCustomValidity('')
      } else {
        email2.el?.setCustomValidity('Emails do not match')
      }
    }
  }, [email1, email2])
  return (
    <div
      className={cx(
        submitted && 'bg-secondary-50',
        !submitted && 'bg-white',
        'p-4 sm:p-8 dark:bg-secondary-800 rounded-lg shadow-md border transition-colors duration-500 ease-in-out'
      )}
      id="support-team"
    >
      <div className={cx(submitted && 'hidden')}>
        <div className="dark:text-secondary-50">
          <h2 className="text-2xl mb-2">{t('Contact Our Support Team')}</h2>
          <p className="mb-4">{t('For technical, billing, and product questions.')}</p>
        </div>
        <div>
          <Form
            id="contact-support"
            name="Contact Support"
            submitText={t('Contact Support')}
            functionPath="https://func.bitwarden.com/api/newhelpdeskticket"
            transformFormData={(formData) => {
              const location = formData.get('selfHosting') === 'No' ? 'Cloud' : 'Self-Hosting'
              formData.set('subject', `${formData.get('subject')} - ${location} - ${formData.get('product')} `)
              return formData
            }}
            afterSubmit={() => {
              setSubmitted(true)
              document.querySelector('#support-team')?.scrollIntoView({
                behavior: 'smooth',
              })
            }}
          >
            <div className="grid gap-2">
              <TextField name="name" type="text" label={t('Name')} required autoFocus />
              <TextField
                name="email"
                type="email"
                label={t('Bitwarden account email')}
                required
                pattern={emailRegex}
                onUpdate={(val, el) => setEmail1({ val, el })}
              />
              <TextField
                name="confirm"
                type="email"
                label={t('Verify account email')}
                required
                pattern={emailRegex}
                onUpdate={(val, el) => setEmail2({ val, el })}
              />
              <SelectField name="product" label={t('Product')} required initialValue="Password Manager">
                {products.map((product) => (
                  <option key={product.key} value={product.key}>
                    {product.label}
                  </option>
                ))}
              </SelectField>
              <RadioFieldset
                label={t('Are you self-hosting?')}
                initialValue="No"
                fields={[
                  { key: 'No', label: t('No') },
                  { key: 'Yes', label: t('Yes') },
                ]}
                name="selfHosting"
                required
                tooltip={t('If you are unsure, choose no.')}
              />
              <SelectField name="subject" label={t('Subject')} required>
                {subjects.map((subject) => (
                  <option key={subject.key} value={subject.key}>
                    {subject.label}
                  </option>
                ))}
              </SelectField>
              <TextArea name="message" label={t('Message...')} required rows={4} />
            </div>
          </Form>
        </div>
      </div>
      {submitted && (
        <div>
          <h3>{t('Thank you for reaching out!')}</h3>
          <p>{t('A confirmation email has been sent to')}:</p>

          <h4 className="flex flex-row items-center mt-3 mb-6 bg-white text-indigoBlue text-sm font-bold sm:font-normal sm:text-xl rounded-md py-2 px-3 border-b-4 border-b-primaryBlue">
            <div className="flex-none">
              <SubmittedImage className="hidden sm:block fill-none w-[45px] h-auto mr-3" />
            </div>
            <span className="flex-grow">{email1?.val}</span>
          </h4>
          <p>{t('Please check your inbox or spam folder to ensure the address is correct.')}</p>
          <p>
            {t(
              'If you can’t access your account email, please submit a new request with a reachable contact email so the support team can follow up.'
            )}
          </p>
          <p>
            {t('In the meantime, feel free to explore our')}{' '}
            <Link to="/help/" className="blue-link">
              Help Center
            </Link>{' '}
            or{' '}
            <Link to="https://community.bitwarden.com" className="blue-link">
              Community Forums
            </Link>
          </p>
        </div>
      )}
    </div>
  )
}
